import type { OrderBy, SortDirection } from '@ofmos/shared/record-query';
import type { FieldMetadata, ObjectMetadata, RecordRow } from '../../../data/client';
import type { FieldContextValue } from '../../../fields/FieldContext';
import { fieldAlign, fieldColumnWidth, isSelectableField } from '../../../fields/fieldTypeMeta';
import { checkpointInitials } from '../../record/checklist';

/**
 * The table's projection of metadata: which columns exist, how wide they are, what
 * titles a row. Pure functions in one file, because the desktop table and the
 * mobile card list must project the SAME thing - a card that shows different
 * fields than the table it replaces is two products.
 */

/** Field values a mobile card carries under the title. Three fits a phone row. */
export const CARD_SECONDARY_FIELDS = 3;

/**
 * HOW MANY ROWS ONE WALK MAY LOAD (`table-completion.md` D3). A cap on the WALK, not on a
 * page: `PAGE_SIZE_CHOICES` decides how many rows arrive per click, this decides how many
 * clicks there can be.
 *
 * Above it the list TRUNCATES VISIBLY rather than refusing, which is
 * `shared/record-group.ts`'s own test: a shortened list is on a screen that can carry a
 * sentence, next to the two controls that fix it - the filter and the export. A refusal
 * would be the `CURRENCY_CODES_MAX` treatment applied to a case that is not its case; a
 * dropped currency code is a missing sum, while row 1,001 is a row nobody has asked
 * narrowly enough for yet.
 *
 * THE PREDICATE COUNTS FETCHED ROWS - `pages.length * pageSize` - AND IT IS `>=`, while
 * the sentence names DISPLAYED rows. The two differ, because duplicate ids are removed at
 * the flatten: an equality over the displayed count would step from 999 to 1,199 on one
 * removed duplicate and the ceiling would never fire. And a COMPLETE walk is never called
 * truncated, whatever the fetched count (D8's precedence).
 *
 * Twenty pages at the default page size, five at the largest choice, and the same order of
 * magnitude as `EXPORT_SELECTION_MAX` (`shared/csv.ts`), which is this product's existing
 * answer to how many rows one person works with at once. It is a JUDGMENT and it is not
 * measured (honesty register 2), which makes it the cheapest constant here to change.
 */
export const TABLE_LOADED_ROWS_MAX = 1_000;

/**
 * HAS THE WALK RUN OUT OF ROOM (`table-completion.md` D3, obligation 4).
 *
 * IT COUNTS PAGES, WHICH IS WHY IT CANNOT BE HANDED A DISPLAYED-ROW COUNT BY MISTAKE. The
 * predicate is `pages.length * pageSize >= TABLE_LOADED_ROWS_MAX`: FETCHED rows, not the rows
 * on screen. `pagedIds` removes duplicate ids at the flatten, so an equality over the
 * displayed count would step from 999 to 1,199 on one removed duplicate and the ceiling would
 * never fire - it would bound neither the DOM nor the refetch, and nothing would say so.
 * `>=` for the same reason, one layer down: no page size and no dedupe can step over it.
 *
 * IT DECIDES THE BUTTON, NEVER THE SENTENCE. A walk that reaches the ceiling on the page that
 * also exhausts the filter is COMPLETE rather than truncated, so `hasNextPage` outranks this
 * predicate in the status row's precedence (D8) - and the sentence names displayed rows
 * whatever this answers.
 */
export function atLoadedRowsCeiling(pageCount: number, pageSize: number): boolean {
  return pageCount * pageSize >= TABLE_LOADED_ROWS_MAX;
}

/**
 * THE DENOMINATOR THE STATUS ROW IS ALLOWED TO PRINT (`table-completion.md` D9).
 *
 * `hasNextPage` and the total come from DIFFERENT STATEMENTS AT DIFFERENT TIMES: the first
 * from the page's own `limit + 1`, the second from the count entry. If rows were deleted
 * between the two, the rows on screen can EXCEED the total. The button is driven by
 * `hasNextPage`, which is the only exact answer to "is there another row after the last one I
 * hold"; the SENTENCE's denominator is driven by the count, and only while it is not already
 * contradicted by what the reader can see.
 *
 * A NUMBER KNOWN TO BE WRONG IS WITHHELD, NEVER GUESSED AND NEVER CLAMPED. The sentence then
 * says how many rows are shown and stops, which is D8's shape 4 - true about the screen, and
 * it becomes the counted shape the moment a fresh count lands.
 *
 * AND IT IS A PURE FUNCTION, which is the whole of the mechanism. The first draft of D9 had
 * the render INVALIDATE the count on disagreement, and that has no fixpoint: rows arriving
 * between the two statements - a CSV import, a workflow fan-out, a merge run - leave the fresh
 * count below the displayed row count, so the predicate is still true, the render invalidates
 * again, and each iteration is a full scan of the filter. What repairs the count is the
 * `records` frame the writes emitted, exactly as everywhere else in this module.
 */
export function usableTotalCount(
  displayedRows: number,
  totalCount: number | undefined,
): number | undefined {
  if (totalCount === undefined) return undefined;
  return displayedRows <= totalCount ? totalCount : undefined;
}

/** Minimum width of the title column in rem: a truncated name is a dead end. */
const TITLE_COLUMN_WIDTH = 16;

export interface TableColumn {
  readonly field: FieldMetadata;
  /** The label-identifier field: first column, never hidden, links to the record. */
  readonly isTitle: boolean;
  readonly align: 'left' | 'right';
  /** Width hint in rem, derived from the field type and its settings. */
  readonly width: number;
  /**
   * `phase-b-coach.md` D5: the head shows the label's INITIALS, with the full label in a
   * tooltip. Four rem cannot hold "48-hour check-in", and eight columns truncated to their
   * first six characters are eight identical prefixes. Absent on every other column, so a
   * header is the label unless something says otherwise.
   */
  readonly headerAbbreviation?: string | undefined;
}

export interface SortState {
  readonly fieldId: string;
  readonly direction: SortDirection;
}

export function tableColumns(
  object: ObjectMetadata,
  fields: readonly FieldMetadata[],
): readonly TableColumn[] {
  const isTitle = (field: FieldMetadata) => field.id === object.labelIdentifierFieldId;
  /**
   * NO MANY_TO_MANY COLUMN (record-page-and-views.md 1.2). The table selects every
   * column's field id, and selecting a MANY_TO_MANY returns `null` at HTTP 200 - so the
   * column would show an em dash on every row of every page, forever, and the table
   * would be issuing the one query shape the platform silently fails.
   */
  const selectable = fields.filter(isSelectableField);
  // The identifier leads regardless of its position: a list is read by its titles.
  const ordered = [...selectable.filter(isTitle), ...selectable.filter((field) => !isTitle(field))];

  return ordered.map((field) => {
    const checkpoint = field.options?.checkpoint !== undefined && field.type === 'DATE';
    return {
      field,
      isTitle: isTitle(field),
      align: fieldAlign(field.type),
      width: isTitle(field)
        ? Math.max(fieldColumnWidth(field.type, field.options), TITLE_COLUMN_WIDTH)
        : fieldColumnWidth(field.type, field.options),
      ...(checkpoint && !isTitle(field)
        ? { headerAbbreviation: checkpointInitials(field.label) }
        : {}),
    };
  });
}

/** The title column ignores the hidden list: a row without its title is unusable. */
export function visibleColumns(
  columns: readonly TableColumn[],
  hiddenFieldIds: readonly string[],
): readonly TableColumn[] {
  return columns.filter((column) => column.isTitle || !hiddenFieldIds.includes(column.field.id));
}

export function sortToOrderBy(sort: SortState | null): readonly OrderBy[] | undefined {
  return sort === null ? undefined : [{ fieldId: sort.fieldId, direction: sort.direction }];
}

/**
 * What titles a record here. Never the raw uuid: an id in a list is noise a user
 * cannot act on, and "Untitled" at least says the title is missing.
 */
export function recordTitle(record: RecordRow, titleFieldId: string): string {
  const value = record.values[titleFieldId];
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return 'Untitled';
}

/**
 * The context a read-only field renders from. Displays ignore `onChange`, but the
 * contract requires one, and `humanOwned` is read off the record's provenance
 * (INV-5) rather than guessed at the call site.
 */
export function displayContext(
  objectId: string,
  record: RecordRow,
  field: FieldMetadata,
): FieldContextValue {
  const relationLabel = record.relationLabels?.[field.id];
  return {
    objectId,
    recordId: record.id,
    field,
    value: record.values[field.id] ?? null,
    onChange: () => undefined,
    readOnly: true,
    humanOwned: record.provenance[field.id] !== undefined,
    /**
     * `file-attachments.md` D18: this is a table ROW, so a FILES value renders as the first
     * label plus a count rather than as the record page's vertical list. Every other display
     * ignores it.
     */
    dense: true,
    /**
     * `phase-b-coach.md` D5: a checkpoint cell draws a warning ring only when it can COMPUTE
     * the deadline, and the deadline counts from another column of the same row. The row is
     * already in hand here, so handing it over costs nothing; a view that did not select the
     * base column simply has no value under that key and the cell draws a plain ring.
     */
    row: record.values,
    // D3: absent unless a QUERY resolved it, which is what stops a table of fourteen
    // rows pointing at fourteen distinct records making fourteen requests.
    ...(relationLabel === undefined ? {} : { relationLabel }),
  };
}
